import React from 'react'
import { Navigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { featureFlags } from '../../config/featureFlags'
import { GovernanceListingSection } from '../../components/governance/GovernanceListingSection'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import ControlHealthCard from '../../components/control-assurance/ControlHealthCard'
import { Button } from '../../components/ui/Button'
import {
  fetchControlHealth,
  fetchDegradedControls,
  fetchOperationalDrift,
  fetchAutomatedEvidence,
  recomputeControlAssurance,
  verifyOperationalControl,
  type OperationalControlStateRow,
} from '../../services/controlAssuranceApi'

type Tab = 'health' | 'degraded' | 'drift' | 'automated'

export default function ComplianceCenterOperationalAssuranceWorkspace() {
  const { t } = useI18n()
  const [tab, setTab] = React.useState<Tab>('health')
  const [loading, setLoading] = React.useState(true)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [states, setStates] = React.useState<OperationalControlStateRow[]>([])
  const [degraded, setDegraded] = React.useState<OperationalControlStateRow[]>([])
  const [drift, setDrift] = React.useState<Array<{ id: string; driftType: string; severity: string; description: string }>>([])
  const [automated, setAutomated] = React.useState<Array<{ id: string; title: string; verificationStatus?: string }>>([])

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [h, d, dr, ae] = await Promise.all([
        fetchControlHealth(),
        fetchDegradedControls(),
        fetchOperationalDrift(),
        fetchAutomatedEvidence(),
      ])
      setStates(h.states || [])
      setDegraded(d.degraded || [])
      setDrift(dr.drift || [])
      setAutomated(ae.automatedEvidence || [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load assurance data')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void load()
  }, [load])

  const handleRecompute = async () => {
    setBusy(true)
    try {
      await recomputeControlAssurance()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Recompute failed')
    } finally {
      setBusy(false)
    }
  }

  const handleVerify = async (id: string) => {
    setBusy(true)
    try {
      await verifyOperationalControl(id)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Verify failed')
    } finally {
      setBusy(false)
    }
  }

  if (!featureFlags.CONTROL_ASSURANCE_V1) {
    return <Navigate to="/compliance-center/overview" replace />
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'health', label: t('controlAssurance.controlHealth', 'Control health') },
    { id: 'degraded', label: t('controlAssurance.operationallyDegraded', 'Operationally degraded') },
    { id: 'drift', label: t('controlAssurance.controlDrift', 'Control drift') },
    { id: 'automated', label: t('controlAssurance.automatedEvidence', 'Automated evidence') },
  ]

  return (
    <div className={listStyles.pageStack}>
      <GovernanceListingSection
        title={t('controlAssurance.operationalAssurance', 'Operational assurance')}
        description={t(
          'controlAssurance.workspaceIntro',
          'Continuously evaluated operational control posture from existing telemetry — deterministic, explainable, not real-time certification.',
        )}
        toolbar={
          <Button type="button" size="small" color="brand" disabled={busy || loading} onClick={handleRecompute}>
            {t('controlAssurance.recompute', 'Recompute assurance')}
          </Button>
        }
      >
        <div className="flex flex-wrap gap-2 mb-3">
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={`text-xs px-3 py-1.5 rounded-md border ${
                tab === item.id
                  ? 'bg-white border-blue-200 text-blue-800 font-medium shadow-sm'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
        {loading ? <p className="text-sm text-slate-500">{t('common.loading', 'Loading…')}</p> : null}
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
      </GovernanceListingSection>

      {tab === 'health' && !loading ? (
        <GovernanceListingSection title={t('controlAssurance.controlHealth', 'Control health')}>
          <div className="flex flex-col gap-2">
            {states.map((s) => (
              <ControlHealthCard key={s.id} state={s} busy={busy} onVerify={handleVerify} />
            ))}
            {states.length === 0 ? <p className="text-xs text-slate-500 m-0">{t('controlAssurance.noStates', 'Run recompute to materialize operational control states.')}</p> : null}
          </div>
        </GovernanceListingSection>
      ) : null}

      {tab === 'degraded' && !loading ? (
        <GovernanceListingSection title={t('controlAssurance.operationallyDegraded', 'Operationally degraded')}>
          <div className="flex flex-col gap-2">
            {degraded.map((s) => (
              <ControlHealthCard key={s.id} state={s} busy={busy} onVerify={handleVerify} />
            ))}
            {degraded.length === 0 ? <p className="text-xs text-slate-500 m-0">{t('controlAssurance.noDegraded', 'No degraded controls.')}</p> : null}
          </div>
        </GovernanceListingSection>
      ) : null}

      {tab === 'drift' && !loading ? (
        <GovernanceListingSection title={t('controlAssurance.operationalDrift', 'Operational drift')}>
          <ul className="m-0 p-0 list-none space-y-2">
            {drift.map((d) => (
              <li key={d.id} className="text-xs border border-slate-100 rounded p-2 bg-slate-50">
                [{d.severity}] {d.driftType}: {d.description}
              </li>
            ))}
            {drift.length === 0 ? <li className="text-xs text-slate-500">{t('controlAssurance.noDrift', 'No open drift events.')}</li> : null}
          </ul>
        </GovernanceListingSection>
      ) : null}

      {tab === 'automated' && !loading ? (
        <GovernanceListingSection title={t('controlAssurance.telemetryBackedEvidence', 'Telemetry-backed evidence')}>
          <ul className="m-0 p-0 list-none space-y-2">
            {automated.map((ev) => (
              <li key={ev.id} className="text-xs border border-slate-100 rounded p-2 bg-slate-50">
                {ev.title} — {ev.verificationStatus || 'PENDING'}
              </li>
            ))}
            {automated.length === 0 ? (
              <li className="text-xs text-slate-500">{t('controlAssurance.noAutomatedEvidence', 'No automated evidence ingested yet.')}</li>
            ) : null}
          </ul>
        </GovernanceListingSection>
      ) : null}
    </div>
  )
}
